import { useFragment } from '@apollo/client';

import {
  ProductUserStatus,
  ProductVisibleStatus,
} from '@models';
import { useCreateProductDialog } from '@hook/useCreateProductDialog';
import { Skeleton } from '@ui/Skeleton';
import { ProductDetailsMessageForm, FormData } from '@components/product/ProductDetailsMessageForm';

import {
  ProductDetailsMessageFormFragment,
  ProductDetailsMessageFormFragmentDoc,
  useProductDetailsMessageFormThreadQuery,
  useProductDetailsMessageFormViewerQuery,
} from './gql/graphql.v3.generated';

type Product = {
  __typename?: 'ProductDetails',
  id: number,
}

interface Props {
  className?: string,
  product?: Product,
}

export const ProductDetailsMessageFormContainer: React.FC<Props> = ({
  className,
  product,
}) => {
  const { data: productData, complete: productDataComplete } = useFragment<ProductDetailsMessageFormFragment>({
    fragment: ProductDetailsMessageFormFragmentDoc,
    from: product || null,
  });

  const {
    data: viewerData,
    loading: viewerLoading,
  } = useProductDetailsMessageFormViewerQuery({
    context: {
      batch: true,
      ignoreAuthError: true,
    },
  });

  const {
    data: threadData,
    loading: threadLoading,
  } = useProductDetailsMessageFormThreadQuery({
    variables: {
      dialogId: productData?.dialogId as number,
    },
    context: {
      batch: true,
    },
    skip: !productData?.dialogId,
  });

  const handleCreateDialog = useCreateProductDialog();

  const handleSubmit = async (formData: FormData) => {
    if (!formData.body || !productData.id) {
      return null;
    }

    const errors = await handleCreateDialog({
      productId: productData.id,
      body: formData.body,
    });

    return errors;
  };

  const canStartDialog = (
    productData?.userStatusTitle !== ProductUserStatus.Hidden
    && productData?.statusTitle === ProductVisibleStatus.Available
    && !productData.seller?.isUserRestrictedByBankId
  );

  if (!productDataComplete || viewerLoading || threadLoading) {
    return (
      <div className={className}>
        <Skeleton
          width='100%'
          height='80px'
        />
      </div>
    );
  }

  if (productData && viewerData && canStartDialog) {
    return (
      <ProductDetailsMessageForm
        className={className}
        user={viewerData?.viewer}
        threadId={threadData?.viewer.thread?.threadId}
        onSubmit={handleSubmit}
      />
    );
  }

  return null;
};
